import { useState } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from 'src/components/application/defaultComponents'
import PrebuiltRenewalFooter from 'src/components/application/PrebuiltRenewalFooter'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData, ClaimData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { TbFileCheck } from 'react-icons/tb'
import { numberToCurrency } from 'src/utils/numberFormat'
import { useRenewal } from 'src/context/renewal'
import { RenewalData } from 'src/dtos/renewalData'
import { isJudgeClaimsTriggered } from 'src/utils/renewals'
import {FILE_SIZE_LABEL} from "../../../../utils/constants";

const Questions = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { renewalData, saveRenewalData } = useRenewal()
  const [reviewData, setReviewData] = useState<ReviewData>(renewalData?.judgeRenewalSection?.questions?.reviewData ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!renewalData?.judgeRenewalSection?.questions?.reviewData?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!renewalData?.judgeRenewalSection?.questions?.reviewData?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/customer/renew/judge/questions/main',
      search: createSearchParams({ review: 'questions' }).toString()
    })
  }

  const updatedApplication = (): RenewalData => ({
    ...renewalData,
    stoppedAt: pathname,
    judgeRenewalSection: {
      ...renewalData?.judgeRenewalSection,
      questions: {
        ...renewalData?.judgeRenewalSection?.questions,
        reviewData
      }
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveRenewalData(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...renewalData,
    stoppedAt: pathname,
    judgeRenewalSection: {
      ...renewalData?.judgeRenewalSection,
      questions: {
        ...renewalData?.judgeRenewalSection?.questions,
        reviewData: {
          ...renewalData?.judgeRenewalSection?.questions?.reviewData,
          [key]: value
        }
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReviewData(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveRenewalData(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(reviewData?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReviewData(prev => ({ ...prev, files: updatedFiles }))
      await saveRenewalData(applicationFileAttached)
    } else {
      setReviewData(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveRenewalData(applicationFileAttached)
    }
  }

  const questionsSection = renewalData?.judgeRenewalSection?.questions
  const claims = questionsSection?.claims

  const hasClaims = isJudgeClaimsTriggered(renewalData)

  const willRenderClaims = hasClaims && claims && claims.length > 0

  const ClaimComponent = ({ claim, index }: { claim: ClaimData, index: number }) => (
    <section>
      <h5>CLAIM {index + 1}</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{claim?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Claimant’s Full Name</strong>
        <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Claim Type</strong>
        <span>{claim?.claimType ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Date of Claim</strong>
        <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Date of Alleged Error</strong>
        <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Is this claim closed?</strong>
        <span>{claim?.isClaimClosed ?? 'N/A'}</span>
        {claim?.isClaimClosed === 'Yes' ? (
          <>
            <span>
              <h6>Indicate how the claim was closed: </h6>
              {claim?.closingType ?? 'N/A'}
            </span>
            <span>
              <h6>Total loss paid including deductibles: </h6>
              {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
            </span>
            <span>
              <h6>Defense cost paid: </h6>
              {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
            </span>
          </>
        ) : claim?.isClaimClosed === 'No' ? (
          <>
            <span>
              <h6>Claimant’s settlement demand: </h6>
              {claim?.claimantsSettlementDemand ? numberToCurrency(claim?.claimantsSettlementDemand) : 'N/A'}
            </span>
            <span>
              <h6>Defendant’s offer for settlement: </h6>
              {claim?.defendantsSettlementOffer ? numberToCurrency(claim?.defendantsSettlementOffer) : 'N/A'}
            </span>
            <span>
              <h6>Insurer’s loss reserve: </h6>
              {claim?.insurersLossReserve ? numberToCurrency(claim?.insurersLossReserve) : 'N/A'}
            </span>
            <span>
              <h6>Deductible: </h6>
              {claim?.closingCaseDeductible ? numberToCurrency(claim?.closingCaseDeductible) : 'N/A'}
            </span>
            <span>
              <h6>Is claim in suit?: </h6>
              {claim?.isClaimInSuit ?? 'N/A'}
            </span>
            {claim?.isClaimInSuit === 'Yes' ? (
              <span>
                <h6>Amount asked in summons: </h6>
                {claim?.amountAskedSummons ? numberToCurrency(claim?.amountAskedSummons) : 'N/A'}
              </span>
            ) : null}
            <span>
              <h6>Total loss pain including deductibles: </h6>
              {claim?.totalLossPain ? numberToCurrency(claim?.totalLossPain) : 'N/A'}
            </span>
          </>
        ) : null}
      </article>
      <article>
        <strong>Name of Insurer</strong>
        <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Description of Claim</strong>
        <span>
          <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
        </span>
        <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
        </span>
        <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
        </span>
        <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
        <span>
          <h6>Upload any applicable claim information:</h6>
        </span>
        {claim?.claimFile?.map(file => (
          <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            {!!file?.fileName ? <TbFileCheck /> : null}
            {file?.fileName ?? 'N/A'}
          </span>
        ))}
      </article>
    </section>
  )

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review the renewal information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='questionsData'>
          <header>
            <div>
              <h4>Renewal Questions</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            <div className='column'>
              <section>
                <h5>QUESTIONS</h5>
                <article>
                  <strong>In the last 12 months, has the applicant been the subject of a professional or judicial liability claim?</strong>
                  <span>{handleYesNoAnswers(questionsSection?.hasBeenSubjectOfClaim)}</span>
                  {questionsSection?.hasBeenSubjectOfClaim === true ? (
                    <span><h6>Has the claim been previously reported to TLIE? </h6>{handleYesNoAnswers(questionsSection?.hasClaimBeenReported)}</span>
                  ) : null}
                </article>
                <article>
                  <strong>Does the applicant know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional or judicial liability claim against the applicant?</strong>
                  <span>{handleYesNoAnswers(questionsSection?.hasBasisForClaim)}</span>
                  {questionsSection?.hasBasisForClaim === true ? (
                    <span><h6>Has the claim been previously reported to TLIE? </h6>{handleYesNoAnswers(questionsSection?.hasBasisForClaimReported)}</span>
                  ) : null}
                </article>
                <article>
                  <strong>Has the applicant been the subject of a judicial disciplinary inquiry or action?</strong>
                  <span>{handleYesNoAnswers(questionsSection?.hasBeenSubjectOfAction)}</span>
                  {questionsSection?.hasBeenSubjectOfAction === true ? (
                    <span>{questionsSection?.hasBeenSubjectOfActionDetails}</span>
                  ) : null}
                </article>
              </section>
            </div>
          </main>
          {(willRenderClaims === true) ? (
            <main className="firm">
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 === 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 !== 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
            </main>
          ) : null}
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={reviewData?.note ?? ''}
                    onChange={e => setReviewData(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!questionsSection?.reviewData?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReviewData(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={reviewData?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/judge/review/basic"
        nextRoute="/customer/renew/judge/sign/signature"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Questions